<template>
  <div v-title="$route.meta.title"
       class="self-training-examination">
    <header class="exam-page-header">
      <div>
        <h2>{{ title || '自助考试' }}</h2>
        <p>共 {{ questions.length }} 题 · 满分 {{ question_score * questions.length }} 分 · 及格 {{ passing_score }}
          分</p>
      </div>
      <div class="exam-countdown"
           :class="{ danger: remainingSeconds < 60 }">
        <i class="el-icon-time"></i> {{ countdownText }}
      </div>
    </header>

    <div class="exam-layout">
      <main class="exam-question-panel"
            v-loading="loading">
        <el-card v-if="currentQuestion"
                 class="question-card">
          <div class="question-index">第 {{ currentIndex + 1 }} 题
            <span style="font-size: 14px">
                  <span id="q-type"
                        class="text-xs text-gray-400">{{ questionsType }}</span>
                  <span v-if="!this.isOpenBook" class="text-xs text-gray-400">（每题 {{ this.question_score }} 分）</span>
            </span>
          </div>
          <h3>{{ currentQuestion.title }}</h3>
          <training-media-preview :key="currentQuestion.id"
                                  class="question-samples"
                                  :files="currentQuestion.sample_files" />
          <!--          单选-->
          <el-radio-group v-if="isSingle(currentQuestion)"
                          :value="answers[currentQuestion.id]"
                          :disabled="submitted || expired"
                          @input="selectAnswer(currentQuestion.id, $event)">
            <el-radio v-for="option in optionsOf(currentQuestion)"
                      :key="optionValue(option)"
                      :label="optionValue(option)">
              {{ optionLabel(option) }}
            </el-radio>
          </el-radio-group>
          <!--          多选-->
          <el-checkbox-group v-else-if="isMultiple(currentQuestion)"
                             :value="multipleAnswer(currentQuestion.id)"
                             :disabled="submitted || expired"
                             @input="selectAnswer(currentQuestion.id, $event)">
            <el-checkbox v-for="option in optionsOf(currentQuestion)"
                         :key="optionValue(option)"
                         :label="optionValue(option)">
              {{ optionLabel(option) }}
            </el-checkbox>
          </el-checkbox-group>
          <!--          填空-->
          <el-input v-else
                    :value="answers[currentQuestion.id]"
                    type="textarea"
                    :disabled="submitted || expired"
                    @input="selectAnswer(currentQuestion.id, $event)"></el-input>
        </el-card>
        <div class="question-actions">
          <el-button :disabled="currentIndex === 0"
                     @click="previousQuestion">上一题
          </el-button>
          <el-button v-if="isOpenBook || currentIndex < questions.length - 1"
                     type="primary"
                     @click="nextQuestion">下一题
          </el-button>
          <el-button v-else
                     type="primary"
                     :loading="submitting"
                     :disabled="submitted || submitting"
                     @click="submitExam(false)">提交答卷
          </el-button>
        </div>
      </main>
      <aside class="answer-card">
        <h3>答题卡</h3>
        <div class="answer-grid">
          <button v-for="(question, index) in questions"
                  :key="question.id"
                  type="button"
                  :class="{ active: index === currentIndex, answered: isAnswered(question.id) }"
                  :disabled="isOpenBook && index > currentIndex"
                  @click="goToQuestion(index)">{{ index + 1 }}
          </button>
        </div>
        <p>已答 {{ answeredCount }} / {{ questions.length }} 题</p>
      </aside>
    </div>

    <el-dialog :visible.sync="feedbackVisible"
               :custom-class="feedbackIsCorrect ? 'open-book-feedback-dialog is-correct' : 'open-book-feedback-dialog is-wrong'"
               width="min(560px, calc(100vw - 32px))"
               append-to-body
               :show-close="false"
               :close-on-click-modal="false"
               :close-on-press-escape="false">
      <div :class="['open-book-feedback', feedbackIsCorrect ? 'is-correct' : 'is-wrong']">
        <div class="open-book-feedback__status">
          <i :class="feedbackIsCorrect ? 'el-icon-circle-check' : 'el-icon-circle-close'"></i>
          {{ feedbackIsCorrect ? '回答正确' : '回答错误' }}
        </div>
        <div class="open-book-feedback__title">正确答案</div>
        <div class="open-book-feedback__answer">
          {{ currentQuestionFeedback.standardAnswerText || '-' }}
        </div>
        <div v-if="currentQuestionFeedback.analysisText || currentQuestionFeedback.analysisFiles.length"
             class="open-book-feedback__analysis">
          <div class="open-book-feedback__title">题目解析</div>
          <p v-if="currentQuestionFeedback.analysisText">{{ currentQuestionFeedback.analysisText }}</p>
          <training-media-preview :files="currentQuestionFeedback.analysisFiles" />
        </div>
      </div>
      <span slot="footer">
        <el-button v-if="!feedbackIsCorrect"
                   type="danger"
                   @click="retryCurrentQuestion">重新作答</el-button>
        <el-button v-else
                   type="success"
                   @click="continueAfterCorrectAnswer">
          {{ currentIndex === questions.length - 1 ? '提交答卷' : '下一题' }}
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { consts as Questionconsts, opts as QuestionOpts } from './../../training-question.constants'
import TrainingMediaPreview from '../../components/TrainingMediaPreview.vue'

const STORAGE_PREFIX = 'self-training-exam:'

function operationId (prefix) {
  return `${prefix || 'operation'}-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

export default {
  name: 'SelfTrainingExamination',
  components: { TrainingMediaPreview },
  data () {
    return {
      title: '',
      questions: [],
      answers: {},
      version: 0,
      passing_score: 0,
      question_score: 0,
      open_book_exam: 0,
      attemptId: '',
      recordId: null,
      deadline: 0,
      remainingSeconds: 0,
      currentIndex: 0,
      loading: false,
      submitting: false,
      submitted: false,
      expired: false,
      feedbackVisible: false,
      feedbackIsCorrect: false,
      timer: null,
      debounceTimer: null,
      pendingSave: null,
      saveQueue: Promise.resolve(),
      pendingSaveOperationId: null,
      pendingSaveOperationVersion: null,
      pendingSubmit: null,
    }
  },
  computed: {
    questionsType () {
      const item = QuestionOpts.questionTypeMaps.find(option => option.value === Number(this.currentQuestion.question_type))
      return item ? item.label : '-'
    },
    currentQuestion () { return this.questions[this.currentIndex] },
    isOpenBook () { return Number(this.open_book_exam) === 1 },
    currentQuestionFeedback () {
      const question = this.currentQuestion
      const analysis = question && question.question_analysis && typeof question.question_analysis === 'object'
        ? question.question_analysis : {}
      return {
        standardAnswerText: question ? this.standardAnswerText(question) : '',
        analysisText: analysis.analysis_text || '',
        analysisFiles: Array.isArray(analysis.analysis_files) ? analysis.analysis_files : [],
      }
    },
    answeredCount () { return this.questions.filter(question => this.isAnswered(question.id)).length },
    countdownText () {
      const minutes = Math.floor(this.remainingSeconds / 60)
      const seconds = this.remainingSeconds % 60
      return `${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`
    },
  },
  created () { this.loadExam() },
  beforeDestroy () {
    clearInterval(this.timer)
    clearTimeout(this.debounceTimer)
  },
  methods: {
    storageKey () { return `${STORAGE_PREFIX}${this.attemptId}` },
    freshOperationId (prefix) { return operationId(prefix) },
    isSingle (question) { return question.question_type === Questionconsts.QUESTION_TYPE_SINGLE },
    isMultiple (question) { return question.question_type === Questionconsts.QUESTION_TYPE_MULTI },
    optionsOf (question) { return question.options || question.option || [] },
    optionValue (option) {
      return option.value !== undefined ? option.value : option.uuid
    },
    optionLabel (option) {
      return option.label || option.name || option.value || option.uuid
    },
    multipleAnswer (id) { return Array.isArray(this.answers[id]) ? this.answers[id] : [] },
    standardAnswerText (question) {
      if (!question || question.standard_answer === undefined || question.standard_answer === null) return ''
      const values = Array.isArray(question.standard_answer) ? question.standard_answer : [question.standard_answer]
      return values.filter(value => value !== null && value !== undefined && value !== '')
        .map(value => {
          const option = this.optionsOf(question).find(item => String(this.optionValue(item)) === String(value))
          return option ? this.optionLabel(option) : String(value)
        })
        .join('、')
    },
    isAnswered (id) {
      const answer = this.answers[String(id)]
      return Array.isArray(answer)
        ? answer.some(item => item !== undefined && item !== null && String(item).trim() !== '')
        : answer !== undefined && answer !== null && String(answer).trim() !== ''
    },
    isAnswerCorrect (question, answer) {
      const standardAnswer = question && question.standard_answer
      if (standardAnswer === undefined || standardAnswer === null || answer === undefined || answer === null) {
        return false
      }
      if (Array.isArray(standardAnswer) || Array.isArray(answer)) {
        if (!Array.isArray(standardAnswer) || !Array.isArray(answer)) return false
        const expected = standardAnswer.map(String).sort()
        const actual = answer.map(String).sort()
        return expected.length === actual.length && expected.every((value, index) => value === actual[index])
      }
      if (Number(question.question_type) === Questionconsts.QUESTION_TYPE_FILL_BLANK) {
        const actualNumber = Number(answer)
        const expectedNumber = Number(standardAnswer)
        return String(answer).trim() !== '' && isFinite(actualNumber) && actualNumber === expectedNumber
      }
      return String(answer) === String(standardAnswer)
    },
    normalizeData (response) {
      const data = response && response.data ? response.data : {}
      return data.data && !data.questions ? data.data : data
    },
    normalizeAnswers (answers) {
      if (!answers || typeof answers !== 'object') return {}
      const normalized = {}
      Object.keys(answers).forEach(key => {
        // 转为字符串，防止出现大量的null值
        if (Array.isArray(answers) && answers[key] === null) return
        normalized[String(key)] = answers[key]
      })
      return normalized
    },
    loadExam () {
      this.loading = true
      const params = {
        id: this.$route.params.id,
        record_id: this.$route.query && this.$route.query.record_id,
        attemptId: this.$route.query && this.$route.query.attemptId,
      }
      return request.common('getSelfTrainingExam', params).then(response => {
        const data = this.normalizeData(response)
        this.title = data.title || data.exam_title || ''
        this.questions = data.questions || data.question_snapshots || []
        this.attemptId = data.attemptId || data.attempt_id || params.attemptId || ''
        this.passing_score = data.passing_score || data.passing_score || ''
        this.question_score = data.question_score || data.question_score || ''
        this.open_book_exam = Number(data.open_book_exam) || 0
        this.recordId = data.record_id || data.recordId || params.record_id
        this.deadline = Number(data.deadline)
        const serverVersion = Number(data.version) || 0
        const serverAnswers = this.normalizeAnswers(data.answers)
        let local
        try { local = JSON.parse(localStorage.getItem(this.storageKey()) || 'null') } catch (e) { local = null }
        if (local && Number(local.version) > serverVersion) {
          this.version = Number(local.version)
          this.answers = this.normalizeAnswers(local.answers)
          this.scheduleSave(true)
        } else {
          this.version = serverVersion
          this.answers = serverAnswers
          this.writeLocalStorage()
        }
        this.tick()
        clearInterval(this.timer)
        this.timer = setInterval(this.tick, 1000)
      }).catch(() => {
        this.$message.error('考试加载失败，请稍后重试')
      }).finally(() => { this.loading = false })
    },
    writeLocalStorage () {
      if (!this.attemptId) return
      try {
        localStorage.setItem(this.storageKey(), JSON.stringify({
          attemptId: this.attemptId,
          version: this.version,
          answers: this.normalizeAnswers(this.answers)
        }))
      } catch (e) {}
    },
    selectAnswer (id, answer) {
      if (this.submitted || this.expired || this.submitting) return
      if (Array.isArray(this.answers)) this.answers = this.normalizeAnswers(this.answers)
      this.$set(this.answers, id, answer)
      this.version += 1
      this.writeLocalStorage()
      this.scheduleSave()
    },
    scheduleSave (immediate) {
      clearTimeout(this.debounceTimer)
      // 同一版本的网络重试复用 operationId；产生新版本时必须生成新的操作号。
      if (!this.pendingSaveOperationId || this.pendingSaveOperationVersion !== this.version) {
        this.pendingSaveOperationId = this.freshOperationId('draft')
        this.pendingSaveOperationVersion = this.version
      }
      const save = () => {
        const operationId = this.pendingSaveOperationId
        const version = this.pendingSaveOperationVersion
        const answers = JSON.parse(JSON.stringify(this.normalizeAnswers(this.answers)))
        // 串行
        this.saveQueue = this.saveQueue.catch(() => {}).then(() => this.saveDraft(operationId, version, answers))
        this.pendingSave = this.saveQueue
        return this.pendingSave
      }
      if (immediate) return save()
      this.pendingSave = new Promise(resolve => {
        this.debounceTimer = setTimeout(() => resolve(save()), 1000)
      })
      return this.pendingSave
    },
    saveDraft (operationId, version, answers) {
      if (this.submitted || (this.expired && !this.submitting) || !this.attemptId) return Promise.resolve()
      const currentOperationId = operationId || (this.pendingSaveOperationId = this.freshOperationId('draft'))
      const currentVersion = version === null || version === undefined ? this.version : version
      return request.dispatch('saveSelfTrainingExamDraft', {
        record_id: this.recordId,
        attemptId: this.attemptId,
        version: currentVersion,
        answers: answers || JSON.parse(JSON.stringify(this.normalizeAnswers(this.answers))),
        operationId: currentOperationId,
      }).then(response => {
        const data = response && response.data ? response.data.detail : {}
        if (Number(data.version) > this.version) this.version = Number(data.version)
        this.writeLocalStorage()
        if (this.pendingSaveOperationId === currentOperationId) {
          this.pendingSaveOperationId = null
          this.pendingSaveOperationVersion = null
        }
        return response
      }).catch(error => {
        this.$message.warning('草稿保存失败，答案已保留在本地')
        throw error
      })
    },
    submitExam (auto) {
      if (this.submitting || this.submitted || !this.attemptId) return this.pendingSubmit || Promise.resolve()
      if (!auto && !this.isOpenBook) {
        console.log(this.answers)
        const unansweredIndex = this.questions.findIndex(question => !this.isAnswered(question.id))
        if (unansweredIndex !== -1) {
          this.currentIndex = unansweredIndex
          this.$message.warning('还有题目未作答，请完成后再提交，已自动跳转到未作答题目位置')
          return Promise.resolve(false)
        }
      }
      this.submitting = true
      // 提交前取消防抖计时并立即把当前版本排入保存队列，避免最后一次选择尚未落 Redis。
      clearTimeout(this.debounceTimer)
      this.scheduleSave(true)
      const waitForSave = this.pendingSave || Promise.resolve()
      this.pendingSubmit = waitForSave.catch(() => {}).then(() => request.common('submitSelfTrainingExam', {
        record_id: this.recordId,
        attemptId: this.attemptId,
        operationId: this.freshOperationId('submit'),
      })).then(response => {
        this.submitted = true
        try { localStorage.removeItem(this.storageKey()) } catch (e) {}
        this.$router.push('/training/self-examination')
        return response
      }).catch(error => {
        this.submitting = false
        throw error
      })
      return this.pendingSubmit
    },
    tick () {
      if (!this.deadline) return
      this.remainingSeconds = Math.max(0, Math.ceil((this.deadline * 1000 - Date.now()) / 1000))
      if (this.remainingSeconds === 0 && !this.expired) {
        this.expired = true
        clearInterval(this.timer)
        this.submitExam(true)
      }
    },
    previousQuestion () { if (this.currentIndex > 0) this.currentIndex -= 1 },
    nextQuestion () {
      if (this.isOpenBook) {
        if (!this.currentQuestion || !this.isAnswered(this.currentQuestion.id)) {
          this.$message.warning('请先作答当前题目')
          return false
        }
        this.feedbackIsCorrect = this.isAnswerCorrect(
          this.currentQuestion,
          this.answers[String(this.currentQuestion.id)]
        )
        this.feedbackVisible = true
        return this.feedbackIsCorrect
      }
      if (this.currentIndex >= this.questions.length - 1) return false
      this.currentIndex += 1
      return true
    },
    retryCurrentQuestion () {
      if (!this.feedbackVisible || this.feedbackIsCorrect || !this.currentQuestion) return false
      this.$delete(this.answers, String(this.currentQuestion.id))
      this.version += 1
      this.feedbackVisible = false
      this.writeLocalStorage()
      this.scheduleSave()
      return true
    },
    continueAfterCorrectAnswer () {
      if (!this.feedbackVisible || !this.feedbackIsCorrect) return false
      this.feedbackVisible = false
      if (this.currentIndex === this.questions.length - 1) {
        return this.submitExam(false)
      }
      this.currentIndex += 1
      return true
    },
    goToQuestion (index) {
      if (this.isOpenBook && index > this.currentIndex) return false
      this.currentIndex = index
      return true
    },
  },
}
</script>

<style scoped
       lang="scss">
.self-training-examination {
  min-height: calc(100vh - 160px);
  padding: 24px 30px;
  background: #f5f7fa;
  color: #183153;
}

.exam-page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;

  h2 {
    margin: 0 0 5px;
  }

  p {
    margin: 0;
    color: #7b8ba1;
  }
}

.exam-countdown {
  padding: 9px 16px;
  border-radius: 8px;
  background: #e8efff;
  color: #2165ff;
  font-size: 18px;
  font-weight: 600;

  &.danger {
    color: #e64d4d;
    background: #fff0f0;
  }
}

.exam-layout {
  display: flex;
  align-items: flex-start;
  gap: 20px;
}

.exam-question-panel {
  flex: 1;
  min-width: 0;
}

.question-card {
  min-height: 330px;

  .question-index {
    color: #2165ff;
    font-size: 20px;
  }

  h3 {
    margin: 18px 0 24px;
    line-height: 1.7;
    font-size: 18px;
    font-weight: 500;
  }

  .el-radio, .el-checkbox {
    display: block;
    margin: 18px 0;
  }
}

.question-samples {
  margin-bottom: 24px;
}

.question-actions {
  display: flex;
  justify-content: space-between;
  margin-top: 18px;
}

.open-book-feedback {
  padding: 16px 18px;
  border-left: 4px solid #64748b;
  border-radius: 0 4px 4px 0;
  background: #f5f7fa;
  color: #344054;
}

.open-book-feedback.is-correct {
  border-left-color: #00a94f;
  background: #effaf3;
  color: #385342;
}

.open-book-feedback.is-wrong {
  border-left-color: #e5484d;
  background: #fff1f1;
  color: #57383a;
}

.open-book-feedback__status {
  margin-bottom: 18px;
  font-size: 20px;
  font-weight: 600;
}

.open-book-feedback__status i {
  margin-right: 8px;
  font-size: 24px;
  vertical-align: -2px;
}

.open-book-feedback.is-correct .open-book-feedback__status,
.open-book-feedback.is-correct .open-book-feedback__title {
  color: #008e43;
}

.open-book-feedback.is-wrong .open-book-feedback__status,
.open-book-feedback.is-wrong .open-book-feedback__title {
  color: #c7373d;
}

.open-book-feedback.is-correct .open-book-feedback__answer {
  color: #006f35;
}

.open-book-feedback.is-wrong .open-book-feedback__answer {
  color: #a6292f;
}

.open-book-feedback__title {
  font-size: 14px;
  font-weight: 600;
}

.open-book-feedback__answer {
  margin-top: 8px;
  font-weight: 600;
}

.open-book-feedback__analysis {
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid rgba(0, 169, 79, .2);
}

.open-book-feedback__analysis p {
  margin: 8px 0 0;
  line-height: 1.7;
}

.answer-card {
  width: 250px;
  padding: 20px;
  border-radius: 10px;
  background: #fff;

  h3 {
    margin: 0 0 18px;
  }

  p {
    color: #7b8ba1;
    font-size: 13px;
  }
}

.answer-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 9px;

  button {
    width: 36px;
    height: 36px;
    border: 1px solid #dfe5ed;
    border-radius: 4px;
    color: #69788d;
    background: #fff;
    cursor: pointer;

    &.answered {
      border-color: #2165ff;
      color: #2165ff;
      background: #e8efff;
    }

    &.active {
      color: #fff;
      background: #2165ff;
    }
  }
}

@media (max-width: 768px) {
  .self-training-examination {
    padding: 20px 16px;
  }
  .exam-layout {
    display: block;
  }
  .answer-card {
    width: auto;
    margin-top: 18px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/training/pages/exam/SelfTrainingExamination.vue